/**
 * Donation source tracking - additive to lib/attribution.js (which stays
 * untouched and keeps driving the existing attrib_* fields). Captures a
 * first-touch and last-touch "src" object into localStorage on every page
 * load, and attaches both to every checkout request so the backend can
 * store src_first/src_last on the donation/subscription record and derive
 * a `channel` field from them.
 */
const FIRST_KEY = "amtm_src_first";
const LAST_KEY = "amtm_src_last";
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const URL_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "gclid"];

function readTouchFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const touch = {};
  URL_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) touch[key] = value;
  });
  touch.landing_page = window.location.pathname;
  touch.referrer = document.referrer || null;
  touch.seen_at = new Date().toISOString();
  return touch;
}

function hasUrlSignal(touch) {
  return URL_KEYS.some((key) => touch[key]);
}

function readStored(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable (private mode / quota) - silently skip
  }
}

/** Call once per page load (RouteTracker). Seeds amtm_src_first on first
 * visit or once it's 30+ days stale; seeds/overwrites amtm_src_last on
 * first visit ever and on every subsequent visit that carries new UTM/
 * click-id params (standard last-touch convention - plain internal
 * navigation never clears a previously captured paid touch). */
export function captureSrcTracking() {
  try {
    const touch = readTouchFromUrl();

    const first = readStored(FIRST_KEY);
    const firstStale = !first || !first.seen_at || Date.now() - new Date(first.seen_at).getTime() > THIRTY_DAYS_MS;
    if (firstStale) writeStored(FIRST_KEY, touch);

    const last = readStored(LAST_KEY);
    if (!last || hasUrlSignal(touch)) writeStored(LAST_KEY, touch);
  } catch {
    // never block navigation on tracking
  }
}

/** Builds the { src_first, src_last } payload to attach to a checkout
 * request. Defaults src_last.utm_campaign to "diwali" when the donor is
 * checking out directly on /diwali and no campaign was already captured. */
export function getSrcTrackingPayload() {
  try {
    const src_first = readStored(FIRST_KEY);
    let src_last = readStored(LAST_KEY);
    if (src_last && !src_last.utm_campaign && window.location.pathname === "/diwali") {
      src_last = { ...src_last, utm_campaign: "diwali" };
    }
    return { src_first, src_last };
  } catch {
    return { src_first: null, src_last: null };
  }
}
